import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon } from '../../../assets/icons/ChevronRight.icon';
import styles from './styles.module.css';

const startOfDay = (d) => {
	const nd = new Date(d);
	nd.setHours(0, 0, 0, 0);
	return nd;
};

const isSameDay = (a, b) => {
	if (!a || !b) return false;
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isBefore = (a, b) => a.getTime() < b.getTime();
const isAfter = (a, b) => a.getTime() > b.getTime();

const clampDate = (date, minDate, maxDate) => {
	if (!date) return date;
	let d = date;
	if (minDate && isBefore(d, minDate)) d = minDate;
	if (maxDate && isAfter(d, maxDate)) d = maxDate;
	return d;
};

const addMonths = (date, count) => {
	const d = new Date(date);
	const day = d.getDate();
	d.setDate(1);
	d.setMonth(d.getMonth() + count);
	const month = d.getMonth();
	d.setDate(Math.min(day, daysInMonth(d.getFullYear(), month)));
	return d;
};

const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const isWithin = (date, start, end) => {
	if (!date || !start || !end) return false;
	const t = startOfDay(date).getTime();
	const s = startOfDay(start).getTime();
	const e = startOfDay(end).getTime();
	return t >= s && t <= e;
};

const getMonthMatrix = (monthDate, weekStartsOn = 0) => {
	const first = startOfMonth(monthDate);
	const last = endOfMonth(monthDate);
	const offset = (first.getDay() - weekStartsOn + 7) % 7;
	const days = daysInMonth(first.getFullYear(), first.getMonth());

	const cells = [];
	for (let i = 0; i < offset; i++) {
		const d = new Date(first);
		d.setDate(d.getDate() - (offset - i));
		cells.push({ date: d, outside: true });
	}

	for (let i = 1; i <= days; i++) {
		const d = new Date(first.getFullYear(), first.getMonth(), i);
		cells.push({ date: d, outside: false });
	}

	const total = Math.ceil(cells.length / 7) * 7;
	for (let i = cells.length; i < total; i++) {
		const d = new Date(last);
		d.setDate(last.getDate() + (i - cells.length) + 1);
		cells.push({ date: d, outside: true });
	}

	const weeks = [];
	for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
	return weeks;
};

const defaultWeekdayNames = (_locale = 'pt-BR', weekStartsOn = 0) => {
	const base = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
	return base.slice(weekStartsOn).concat(base.slice(0, weekStartsOn));
};

const defaultMonthLabel = (date, locale = 'pt-BR') =>
	new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);

const computeNextSelection = ({
	current,
	clicked,
	selectionMode,
	allowSameDay,
}) => {
	const day = startOfDay(clicked);
	const { startDate, endDate } = current || { startDate: null, endDate: null };

	if (selectionMode === 'start') {
		return { startDate: day, endDate: endDate || null, completed: true, completedWith: 'start' };
	}
	if (selectionMode === 'end') {
		return { startDate: startDate || null, endDate: day, completed: true, completedWith: 'end' };
	}
	if (startDate && endDate) {
		return { startDate: day, endDate: null, completed: false };
	}
	if (!startDate) {
		return { startDate: day, endDate: null, completed: false };
	}
	if (isAfter(day, startDate) || (allowSameDay && isSameDay(day, startDate))) {
		return { startDate, endDate: day, completed: true, completedWith: 'range' };
	}
	return { startDate: day, endDate: null, completed: false };
};

const Day = ({
	date,
	outside,
	isToday,
	disabled,
	isStart,
	isEnd,
	inRange,
	onClick,
	onMouseEnter,
}) => {
	const classNames = [styles.day];
	if (outside) classNames.push(styles.outside);
	if (isToday) classNames.push(styles.today);
	if (disabled) classNames.push(styles.disabled);
	if (inRange) classNames.push(styles.inRange);
	if (isStart) classNames.push(styles.start);
	if (isEnd) classNames.push(styles.end);
	return (
		<button
			type="button"
			className={classNames.join(' ')}
			onClick={disabled ? undefined : () => onClick(date)}
			onMouseEnter={disabled ? undefined : () => onMouseEnter && onMouseEnter(date)}
			aria-pressed={isStart || isEnd || inRange || undefined}
			aria-disabled={disabled || undefined}
			tabIndex={disabled ? -1 : 0}
		>
			{date.getDate()}
		</button>
	);
};

const MonthView = ({
	monthDate,
	weekStartsOn,
	locale,
	selection,
	hoverDate,
	isDateDisabled,
	onDayClick,
	onDayHover,
}) => {
	const today = startOfDay(new Date());
	const weeks = useMemo(() => getMonthMatrix(monthDate, weekStartsOn), [monthDate, weekStartsOn]);

	const renderDay = (cell) => {
		const d = cell.date;
		const outside = cell.outside;
			if (outside) {
				// Do not show outside days; keep grid alignment with a placeholder cell
				return <div key={d.toISOString()} className={styles.placeholder} aria-hidden />;
			}
			const disabled = isDateDisabled ? isDateDisabled(d) : false;
		const isToday = isSameDay(d, today);

		const { startDate, endDate } = selection || { startDate: null, endDate: null };

		const isStart = !!startDate && isSameDay(d, startDate);
		const isEnd = !!endDate && isSameDay(d, endDate);

		let inRange = false;
		if (startDate && endDate) {
			inRange = isWithin(d, startDate, endDate);
		} else if (startDate && hoverDate) {
			const s = isBefore(hoverDate, startDate) ? hoverDate : startDate;
			const e = isAfter(hoverDate, startDate) ? hoverDate : startDate;
			inRange = isWithin(d, s, e);
		}

		return (
			<Day
				key={d.toISOString()}
				date={d}
				outside={outside}
				isToday={isToday}
				disabled={disabled}
				isStart={isStart}
				isEnd={isEnd}
				inRange={inRange}
				onClick={onDayClick}
				onMouseEnter={onDayHover}
			/>
		);
	};

	const weekdayShort = defaultWeekdayNames(locale, weekStartsOn);

	return (
		<div className={styles.month} aria-label={defaultMonthLabel(monthDate, locale)}>
			<div className={styles.weekdays}>
				{weekdayShort.map((w) => (
					<div key={w} className={styles.weekday} aria-hidden>
						{w}
					</div>
				))}
			</div>
			{weeks.map((week, idx) => (
				<div className={styles.week} key={idx} role="row">
					{week.map(renderDay)}
				</div>
			))}
		</div>
	);
};

/**
 * DatePicker component
 * Props:
 * - selectionMode: 'range' | 'start' | 'end' (default 'range')
 * - value: { startDate: Date|null, endDate: Date|null } (controlled)
 * - defaultValue: same shape (uncontrolled)
 * - onChange(selection, meta)
 * - onComplete(selection, meta) called when selection is complete
 * - minDate, maxDate: Date
 * - isDateDisabled: (date: Date) => boolean
 * - weekStartsOn: 0-6 (0=Sun)
 * - locale: BCP47 string (e.g., 'pt-BR')
 * - numberOfMonths: number (default 1)
 * - initialMonth: Date
 * - allowSameDay: boolean (default false)
 */
const DatePicker = ({
	selectionMode = 'range',
	value,
	defaultValue,
	onChange,
	onComplete,
	minDate,
	maxDate,
	isDateDisabled: isDateDisabledProp,
	weekStartsOn = 0,
	locale = 'pt-BR',
	numberOfMonths = 1,
	initialMonth,
	allowSameDay = false,
	className,
	customHeaderClassName,
	variant = 'fullscreen',
	style,
}) => {
	const isControlled = value !== undefined;
	const [internalSelection, setInternalSelection] = useState(() => {
		const initial = value || defaultValue || { startDate: null, endDate: null };
		return {
			startDate: initial.startDate ? startOfDay(initial.startDate) : null,
			endDate: initial.endDate ? startOfDay(initial.endDate) : null,
		};
	});
	const selection = isControlled ? value : internalSelection;

	const normalizedMin = minDate ? startOfDay(minDate) : null;
	const normalizedMax = maxDate ? startOfDay(maxDate) : null;

	const today = startOfDay(new Date());

		const [currentMonth, setCurrentMonth] = useState(() => {
			const base = initialMonth || (selection && selection.startDate) || today;
		const clamped = clampDate(base, normalizedMin, normalizedMax) || today;
		return startOfMonth(clamped);
	});

	useEffect(() => {
			const startSel = selection ? selection.startDate : null;
			if (startSel) {
				const m = startOfMonth(startSel);
				if (!isSameDay(m, startOfMonth(currentMonth))) {
					setCurrentMonth(m);
				}
			}
		}, [selection && selection.startDate]);

	const [hoverDate, setHoverDate] = useState(null);

	const isDateDisabled = useCallback(
		(date) => {
			const d = startOfDay(date);
			if (normalizedMin && isBefore(d, normalizedMin)) return true;
			if (normalizedMax && isAfter(d, normalizedMax)) return true;
			if (typeof isDateDisabledProp === 'function' && isDateDisabledProp(d)) return true;
			return false;
		},
		[normalizedMin, normalizedMax, isDateDisabledProp]
	);

	const commitSelection = useCallback(
		(next, meta) => {
			const payload = { startDate: next.startDate || null, endDate: next.endDate || null };
			if (!isControlled) setInternalSelection(payload);
			if (onChange) onChange(payload, meta);
			if (next.completed && onComplete) onComplete(payload, meta);
		},
		[isControlled, onChange, onComplete]
	);

	const handleDayClick = useCallback(
		(date) => {
			if (isDateDisabled(date)) return;
			const next = computeNextSelection({ current: selection, clicked: date, selectionMode, allowSameDay });
			commitSelection(next, { source: 'day-click' });
		},
		[selection, selectionMode, allowSameDay, isDateDisabled, commitSelection]
	);

	const handleDayHover = useCallback(
		(date) => {
			if (isDateDisabled(date)) return;
			setHoverDate(startOfDay(date));
		},
		[isDateDisabled]
	);

	const goPrev = useCallback(() => setCurrentMonth((m) => addMonths(m, -1)), []);
	const goNext = useCallback(() => setCurrentMonth((m) => addMonths(m, 1)), []);

	const containerRef = useRef(null);

	const months = useMemo(() => {
		return new Array(Math.max(1, numberOfMonths)).fill(0).map((_, i) => addMonths(currentMonth, i));
	}, [currentMonth, numberOfMonths]);

	return (
		<div
			className={[styles.datepicker, variant === 'inline' ? styles.inline : '', className]
				.filter(Boolean)
				.join(' ')}
			style={style}
			ref={containerRef}
		>
			<div className={[styles.header, customHeaderClassName].filter(Boolean).join(' ')}>
				<button type="button" className={styles.nav} onClick={goPrev} aria-label="Mês anterior">
					<ChevronRightIcon size={16} color="#007E47" rotate={180}/>
				</button>
				<div className={styles.monthLabels}>
					{months.map((m, i) => {
						const full = defaultMonthLabel(m, locale); // e.g. "outubro de 2024" ou "outubro 2024"
						// Tentar extrair ano (4 dígitos). Mantém mês com capitalização original.
						const match = full.match(/^(.*?)(?:\s+de\s+)?(\d{4})$/i);
						if (!match) {
							return (
								<div key={i} className={styles.monthLabel} aria-live="polite">{full}</div>
							);
						}
						const monthText = match[1].trim();
						const yearText = match[2];
						return (
							<div key={i} className={styles.monthLabel} aria-live="polite">
								<span>{monthText} </span>
								<span className={styles.monthYear}>{yearText}</span>
							</div>
						);
					})}
				</div>
				<button type="button" className={styles.nav} onClick={goNext} aria-label="Próximo mês">
					<ChevronRightIcon size={16} color="#007E47" />
				</button>
			</div>

			<div className={styles.body}>
				{months.map((m) => (
					<MonthView
						key={`${m.getFullYear()}-${m.getMonth()}`}
						monthDate={m}
						weekStartsOn={weekStartsOn}
						locale={locale}
						selection={selection}
						hoverDate={hoverDate}
						isDateDisabled={isDateDisabled}
						onDayClick={handleDayClick}
						onDayHover={handleDayHover}
					/>
				))}
			</div>
		</div>
	);
};

export default DatePicker;




// WEBPACK FOOTER //
// ./src/components/V2/datepicker/index.jsx